import {useQueryClient} from '@tanstack/react-query';
import {ReactNode, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {ApiStatusCodeVo} from '../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {PagewebError} from '../../errors/PagewebError.class';
import {
  StaticLandingRankingThemeSection,
  StaticLandingRankingThemeSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingRankingThemeSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';

interface Props {
  section: Omit<StaticLandingRankingThemeSection, 'groups'>;
  children: ReactNode;
}

export function StaticLandingRankingThemeMoreFetcher({
  children,
  section,
}: Props) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const rankingThemeSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingRankingThemeSection,
        'groups'
      >
  );
  const {param: currentParam} = rankingThemeSection;
  /**
   * queryKey 안정화를 위해 txId 는 variables.param 에서 제외한다.
   * tx_id 체이닝은 pageParam 으로만 전달한다 (getNextPageParam 참고).
   * 만약 variables.param 에 txId 를 포함하면 Redux section 갱신 →
   * variables 변경 → 새 queryKey → 매번 page=0 부터 재페칭되는 버그가 발생한다.
   */
  const param: StaticLandingRankingThemeSectionQueryVariables['param'] = {
    screenUid: currentParam?.screenUid,
    filterType: currentParam?.filterType,
    sortType: currentParam?.sortType,
  };
  const {
    data,
    isSuccess,
    fetchNextPage,
    isFetching,
    isFetchedAfterMount,
    error,
  } = useInfiniteStaticLandingRankingThemeSectionQuery(
    {
      sectionId: section.id,
      param,
    },
    {
      initialPageParam: {sectionId: section.id, param},
      getNextPageParam: lastPage => {
        const lastSection =
          lastPage.staticLandingRankingThemeSection as StaticLandingRankingThemeSection;
        if (lastSection?.isEnd) {
          return undefined;
        }
        return {
          sectionId: section.id,
          param: {
            ...param,
            page: (lastSection?.param.page || 0) + 1,
            // tx_id 체이닝: 직전 페이지 응답의 tx_id 를 그대로 다음 요청에 전달
            txId: lastSection?.param.txId ?? undefined,
          },
        };
      },
      initialData: rankingThemeSection
        ? {
            pages: [
              {
                staticLandingRankingThemeSection:
                  rankingThemeSection as StaticLandingRankingThemeSection,
              },
            ],
            pageParams: [{sectionId: section.id, param}],
          }
        : undefined,
      ...CACHE_ONLY_CONFIG,
    }
  );
  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingRankingThemeSection as
    | StaticLandingRankingThemeSection
    | undefined;

  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  /**
   * -2802 (API_COMMON_REFRESH_REQUIRED) 수신 시 처리.
   *
   * 7일 초과로 만료된 tx_id 로 요청한 경우 서버가 내려주는 코드로,
   * AC 상 "필터/정렬 상태는 유지한 채 page=0 부터 새 스냅샷으로 재조회" 해야 한다.
   *
   * - axios 인터셉터에서 result_code<0 이면 PagewebError throw → error 로 유입
   * - refetch()/invalidateQueries 는 기존 pageParams(만료 tx_id) 를 유지한 채 재조회하므로 부적합
   * - resetQueries 로 query state 를 리셋하면 initialPageParam(page 없음, txId 없음) 부터 다시 호출되어
   *   새 tx_id 를 서버로부터 재발급받는다. filter/sort 는 variables.param 에 남아 있으므로 자동 유지.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: param 은 매 렌더마다 새 객체이므로 의존성에 넣지 않아 resetQueries 가 무한 루프로 돌지 않도록 방지한다.
  useEffect(() => {
    const resultCode =
      error instanceof PagewebError
        ? error.serializedError.resultCode
        : undefined;
    if (resultCode === ApiStatusCodeVo.RefreshRequired) {
      queryClient.resetQueries({
        queryKey: useInfiniteStaticLandingRankingThemeSectionQuery.getKey({
          sectionId: section.id,
          param,
        }),
      });
    }
  }, [error, queryClient, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
